import {
  DEFAULT_FORM_PARAMS,
  DEFAULT_IMAGE_FORM_PARAMS,
  MODEL_VERSION,
  MODEL_VERSION_SETTINGS_MAP,
} from '@/app/constants';
import type {
  CreationInfo,
  FormImagePrompt,
  ImagePrompt,
  ModelVersion,
  ReferencePrompt,
  SourceVideo,
  SupportedLanguage,
  TaskInfo,
  TaskInputPrompts,
  TaskSettingsVideo2extend,
  TextPromptWithInputItem,
} from '@/app/types';
import { getResourceUrl } from './get-resource-url';
import { getNextImageName } from './rename-image';
import { appendUnMentionedReferences, parseTextToTextPromptWithReference } from './text-prompt-with-reference';
import { getAvailableOptions, isOptionAllowed } from './validate-settings';
import { ceilVideoDurationToSeconds, normalizeDiscreteVideoDurationToSeconds } from './video-duration';

const ORIGINAL_MEDIA_ID_PREFIX = 'original-image-';

export type QuickEditToolMode = 'video' | 'image';

export interface QuickEditToolSettings {
  aspectRatio: string;
  resolution: string;
  duration?: number;
}

export interface QuickEditToolSourceMeta {
  creationId?: string;
  taskId?: string;
  uri?: string;
  nomarkUri?: string;
  coverImageUrl?: string;
  resolution?: CreationInfo['resolution'];
}

export interface QuickEditToolDraft {
  initializedKey?: string;
  dirty: boolean;
  source: QuickEditToolSourceMeta;
  sourceCreationId?: string;
  sourceVideo: SourceVideo | null;
  sourceImage: ImagePrompt | null;
  textPrompt: TextPromptWithInputItem;
  referencePrompts: ReferencePrompt[];
  settings: QuickEditToolSettings;
}

export type Video2ExtendFormParams = {
  modelVersion: ModelVersion;
  textPrompt: string;
  imagePrompts: (FormImagePrompt | null)[];
  videoPrompt: {
    from: number;
    to: number;
    duration: number;
    content: string;
    sourceMeta:
      | {
          type: 'creation';
          creationId: string;
        }
      | {
          type: 'upload';
          uploadId?: string;
        };
  };
  settings: TaskSettingsVideo2extend;
};

export interface ExtensionToolDraft {
  initializedKey?: string;
  dirty: boolean;
  creation: CreationInfo | null;
  rangeValue: [number, number];
  uploadedVideo: SourceVideo | null;
  formParams: Video2ExtendFormParams;
}

export const getQuickEditToolModelVersion = (mode: QuickEditToolMode): ModelVersion => {
  return mode === 'video' ? MODEL_VERSION.Q2_PRO : MODEL_VERSION.Q2;
};

export const buildQuickEditToolSettings = ({
  mode,
  userPlan,
  defaultAspectRatio,
  defaultDuration,
}: {
  mode: QuickEditToolMode;
  userPlan?: string;
  defaultAspectRatio?: string;
  defaultDuration?: number;
}): QuickEditToolSettings => {
  const modelVersion = getQuickEditToolModelVersion(mode);
  const defaultFormParamsForTask = mode === 'image' ? DEFAULT_IMAGE_FORM_PARAMS : DEFAULT_FORM_PARAMS;
  const settingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
  const baseSettings = defaultFormParamsForTask[settingsKey] as
    | { aspect_ratio?: string; resolution?: string; duration?: number }
    | undefined;

  let finalDuration = baseSettings?.duration;

  if (mode === 'video') {
    const availableDurations = getAvailableOptions(modelVersion, 'character2video').duration || [];
    const normalizedDefaultDuration =
      typeof defaultDuration === 'number' ? normalizeDiscreteVideoDurationToSeconds(defaultDuration) : undefined;

    if (availableDurations.length > 0) {
      const isAllowed = (duration: string) => isOptionAllowed('duration', duration, userPlan, modelVersion);
      const toValue = (duration: string) => (duration === 'auto' ? -1 : Number.parseInt(duration, 10));
      const durationString = normalizedDefaultDuration === -1 ? 'auto' : normalizedDefaultDuration?.toString();

      if (durationString && availableDurations.includes(durationString) && isAllowed(durationString)) {
        finalDuration = normalizedDefaultDuration;
      } else {
        const fallbackDuration = availableDurations.find(isAllowed) || availableDurations[0];
        finalDuration = toValue(fallbackDuration);
      }
    }
  }

  return {
    aspectRatio: defaultAspectRatio || baseSettings?.aspect_ratio || (mode === 'image' ? 'intelligent' : '16:9'),
    resolution: baseSettings?.resolution || '1080p',
    duration: finalDuration,
  };
};

export const createEmptyQuickEditToolDraft = (mode: QuickEditToolMode): QuickEditToolDraft => {
  return {
    initializedKey: undefined,
    dirty: false,
    source: {},
    sourceCreationId: undefined,
    sourceVideo: null,
    sourceImage: null,
    textPrompt: [],
    referencePrompts: [],
    settings: buildQuickEditToolSettings({
      mode,
    }),
  };
};

export const buildQuickEditToolDraftFromCreation = ({
  mode,
  creation,
  originalMediaName,
  userPlan,
  defaultAspectRatio,
}: {
  mode: QuickEditToolMode;
  creation: Pick<CreationInfo, 'cover_uri' | 'duration' | 'id' | 'nomark_uri' | 'resolution' | 'uri'>;
  originalMediaName: string;
  userPlan?: string;
  defaultAspectRatio?: string;
}): QuickEditToolDraft => {
  const imagePreviewUrl = creation.nomark_uri || creation.uri || '';
  const videoPreviewUrl = getResourceUrl({
    uri: creation.uri || '',
    noMarkUri: creation.nomark_uri || '',
  });
  const originalMediaId = `${ORIGINAL_MEDIA_ID_PREFIX}${creation.uri || 'uploaded'}`;
  const settings = buildQuickEditToolSettings({
    mode,
    userPlan,
    defaultAspectRatio,
    defaultDuration: creation.duration,
  });

  const referencePrompt: ReferencePrompt =
    mode === 'video'
      ? {
          type: 'video',
          id: originalMediaId,
          name: originalMediaName,
          status: 'done',
          prompt: {
            video: creation.uri || '',
            videoPreview: videoPreviewUrl,
            coverImage: creation.cover_uri,
            coverImagePreview: creation.cover_uri,
          },
        }
      : {
          type: 'image',
          id: originalMediaId,
          name: originalMediaName,
          status: 'done',
          prompt: {
            sourceImage: creation.uri || '',
            sourceImagePreview: imagePreviewUrl,
            selectedImage: creation.uri || '',
            selectedImagePreview: imagePreviewUrl,
            sourceImageWidth: creation.resolution?.width,
            sourceImageHeight: creation.resolution?.height,
            selectedImageWidth: creation.resolution?.width,
            selectedImageHeight: creation.resolution?.height,
          },
        };

  return {
    initializedKey: creation.id ? `creation:${creation.id}` : undefined,
    dirty: false,
    source: {
      creationId: creation.id,
      uri: creation.uri,
      nomarkUri: creation.nomark_uri,
      coverImageUrl: creation.cover_uri,
      resolution: creation.resolution,
    },
    sourceCreationId: creation.id,
    sourceVideo:
      mode === 'video' && creation.uri
        ? {
            id: creation.id,
            src: creation.uri,
            preview: videoPreviewUrl,
            aspectRatio:
              creation.resolution?.width && creation.resolution?.height
                ? creation.resolution.width / creation.resolution.height
                : 16 / 9,
            duration: creation.duration,
            cover_url: creation.cover_uri,
            cover_preview_url: creation.cover_uri,
          }
        : null,
    sourceImage:
      mode === 'image' && creation.uri
        ? {
            sourceImage: creation.uri,
            sourceImagePreview: imagePreviewUrl,
            selectedImage: creation.uri,
            selectedImagePreview: imagePreviewUrl,
            sourceImageWidth: creation.resolution?.width,
            sourceImageHeight: creation.resolution?.height,
            selectedImageWidth: creation.resolution?.width,
            selectedImageHeight: creation.resolution?.height,
          }
        : null,
    textPrompt: [
      {
        type: 'mention',
        mention: { id: originalMediaId, name: originalMediaName, color: '#508DFF' },
      },
    ],
    referencePrompts: creation.uri ? [referencePrompt] : [],
    settings,
  };
};

const getSelectedImageRange = (prompt: TaskInputPrompts): ImagePrompt['selectedImageRange'] => {
  if (!prompt.selected_region) return undefined;

  return [
    prompt.selected_region.top_left.x,
    prompt.selected_region.top_left.y,
    prompt.selected_region.bottom_right.x,
    prompt.selected_region.bottom_right.y,
  ];
};

const getSourceImagePrompt = (prompt: TaskInputPrompts): ImagePrompt => {
  const sourceImage = prompt.src_imgs?.[0] || prompt.content || '';
  const selectedImage = prompt.content || sourceImage;

  return {
    sourceImage,
    sourceImagePreview: sourceImage,
    selectedImage,
    selectedImagePreview: selectedImage,
    selectedImageRange: getSelectedImageRange(prompt),
  };
};

const buildImageReferencePrompt = ({
  prompt,
  fallbackIndex,
  locale,
}: {
  prompt: TaskInputPrompts;
  fallbackIndex: number;
  locale: SupportedLanguage;
}): ReferencePrompt => ({
  type: 'image',
  id: `recreate-image-${fallbackIndex}-${prompt.content || fallbackIndex}`,
  name: prompt.name || getNextImageName(fallbackIndex, locale),
  status: 'done',
  prompt: getSourceImagePrompt(prompt),
});

export const buildQuickEditToolDraftFromTask = ({
  mode,
  task,
  taskId,
  locale,
  userPlan,
  originalVideoName,
  originalImageName,
}: {
  mode: QuickEditToolMode;
  task: Pick<TaskInfo, 'input'> & { settings?: TaskInfo['settings'] };
  taskId: string;
  locale: SupportedLanguage;
  userPlan?: string;
  originalVideoName: string;
  originalImageName: string;
}): QuickEditToolDraft | null => {
  const prompts = task.input?.prompts || [];
  const textContent = prompts.find((prompt) => prompt.type === 'text')?.content || '';
  const settings = buildQuickEditToolSettings({
    mode,
    userPlan,
    defaultAspectRatio: task.settings?.aspect_ratio,
    defaultDuration: typeof task.settings?.duration === 'number' ? task.settings.duration : undefined,
  });

  if (mode === 'video') {
    const sourcePrompt = prompts.find((prompt) => prompt.type === 'video');
    if (!sourcePrompt?.content) return null;

    const originalMediaId = `${ORIGINAL_MEDIA_ID_PREFIX}${sourcePrompt.content}`;
    const sourceReference: ReferencePrompt = {
      type: 'video',
      id: originalMediaId,
      name: sourcePrompt.name || originalVideoName,
      status: 'done',
      prompt: {
        video: sourcePrompt.content,
        videoPreview: sourcePrompt.content,
        coverImage: sourcePrompt.cover_uri,
        coverImagePreview: sourcePrompt.cover_uri,
      },
    };
    const extraReferences = prompts
      .filter((prompt) => prompt.type === 'image')
      .map((prompt, index) => buildImageReferencePrompt({ prompt, fallbackIndex: index + 1, locale }));
    const allReferences = [sourceReference, ...extraReferences];
    const sourceCreationId = sourcePrompt.reference?.creation_id || undefined;

    return {
      initializedKey: `task:${taskId}`,
      dirty: false,
      source: {
        creationId: sourceCreationId,
        taskId,
        uri: sourcePrompt.content,
        nomarkUri: sourcePrompt.content,
        coverImageUrl: sourcePrompt.cover_uri,
      },
      sourceCreationId,
      sourceVideo: {
        id: sourceCreationId || sourcePrompt.content,
        src: sourcePrompt.content,
        preview: sourcePrompt.content,
        aspectRatio: 16 / 9,
        duration: typeof task.settings?.duration === 'number' ? task.settings.duration : undefined,
        cover_url: sourcePrompt.cover_uri,
        cover_preview_url: sourcePrompt.cover_uri,
      },
      sourceImage: null,
      referencePrompts: allReferences,
      textPrompt: appendUnMentionedReferences(
        parseTextToTextPromptWithReference(textContent, allReferences),
        allReferences,
      ),
      settings,
    };
  }

  const imagePrompts = prompts.filter((prompt) => prompt.type === 'image');
  const sourcePrompt = imagePrompts[0];
  if (!sourcePrompt?.content) return null;

  const originalMediaId = `${ORIGINAL_MEDIA_ID_PREFIX}${sourcePrompt.content}`;
  const sourceReference: ReferencePrompt = {
    type: 'image',
    id: originalMediaId,
    name: sourcePrompt.name || originalImageName,
    status: 'done',
    prompt: getSourceImagePrompt(sourcePrompt),
  };
  const extraReferences = imagePrompts
    .slice(1)
    .map((prompt, index) => buildImageReferencePrompt({ prompt, fallbackIndex: index + 1, locale }));
  const allReferences = [sourceReference, ...extraReferences];

  return {
    initializedKey: `task:${taskId}`,
    dirty: false,
    source: {
      taskId,
      uri: sourcePrompt.content,
      nomarkUri: sourcePrompt.content,
    },
    sourceCreationId: undefined,
    sourceVideo: null,
    sourceImage: sourceReference.prompt,
    referencePrompts: allReferences,
    textPrompt: appendUnMentionedReferences(
      parseTextToTextPromptWithReference(textContent, allReferences),
      allReferences,
    ),
    settings,
  };
};

export const createEmptyExtensionToolDraft = (): ExtensionToolDraft => ({
  initializedKey: undefined,
  dirty: false,
  creation: null,
  rangeValue: [0, 0],
  uploadedVideo: null,
  formParams: {
    modelVersion: MODEL_VERSION.Q2,
    settings: {
      sample_count: 1,
      schedule_mode: 'normal',
      resolution: '1080p',
      duration: 4,
      transition: 'extendspeed',
    },
    textPrompt: '',
    imagePrompts: [],
    videoPrompt: {
      from: 0,
      to: 0,
      duration: 0,
      content: '',
      sourceMeta: {
        type: 'creation',
        creationId: '',
      },
    },
  },
});

export const buildExtensionToolDraftFromCreation = (creation: CreationInfo): ExtensionToolDraft => {
  const totalDuration = ceilVideoDurationToSeconds(creation.duration);

  return {
    initializedKey: creation.id ? `creation:${creation.id}` : undefined,
    dirty: false,
    creation,
    rangeValue: [0, totalDuration],
    uploadedVideo: null,
    formParams: {
      modelVersion: MODEL_VERSION.Q2,
      settings: {
        sample_count: 1,
        schedule_mode: 'normal',
        resolution: '1080p',
        duration: 4,
        transition: 'extendspeed',
      },
      textPrompt: '',
      imagePrompts: [],
      videoPrompt: {
        from: 0,
        to: totalDuration,
        duration: totalDuration,
        content: `sscreation:?id=${creation.id || ''}`,
        sourceMeta: {
          type: 'creation',
          creationId: creation.id || '',
        },
      },
    },
  };
};

const parseExtendVideoPromptMeta = ({
  content,
  creationId,
}: {
  content?: string;
  creationId?: string;
}): { content: string; sourceMeta: Video2ExtendFormParams['videoPrompt']['sourceMeta'] } => {
  const isCreationSource = !!creationId && creationId !== '0';

  return {
    content: isCreationSource ? `sscreation:?id=${creationId}` : content || '',
    sourceMeta: isCreationSource
      ? {
          type: 'creation',
          creationId,
        }
      : {
          type: 'upload',
        },
  };
};

export const buildExtensionToolDraftFromTask = ({
  task,
  taskId,
  sourceCreationId,
}: {
  task: Pick<TaskInfo, 'input'> & { settings?: TaskInfo['settings'] };
  taskId: string;
  sourceCreationId?: string;
}): ExtensionToolDraft | null => {
  const prompts = task.input?.prompts || [];
  const videoPrompt = prompts.find((prompt) => prompt.type === 'video');

  if (!videoPrompt) {
    return null;
  }

  const { content, sourceMeta } = parseExtendVideoPromptMeta({
    content: videoPrompt.content,
    creationId: task.input?.creation_id || sourceCreationId,
  });
  const sourceDuration = ceilVideoDurationToSeconds(videoPrompt.extend2video?.duration);
  const rangeFrom = Math.max(0, Math.floor(videoPrompt.extend2video?.from || 0));
  const rangeTo = Math.max(rangeFrom, Math.floor(videoPrompt.extend2video?.to || sourceDuration));
  const imagePrompts = prompts
    .filter((prompt) => prompt.type === 'image')
    .map((prompt, index) => ({
      id: `extend-image-${taskId}-${index}`,
      status: 'done' as const,
      prompt: getSourceImagePrompt(prompt),
    }));

  return {
    initializedKey: `task:${taskId}`,
    dirty: false,
    creation: null,
    rangeValue: [rangeFrom, rangeTo],
    uploadedVideo: videoPrompt.content
      ? {
          id: sourceMeta.type === 'creation' ? sourceMeta.creationId : content,
          src: videoPrompt.content,
          preview: videoPrompt.content,
          aspectRatio: 16 / 9,
          duration: sourceDuration,
        }
      : null,
    formParams: {
      modelVersion: (task.settings?.model_version as ModelVersion) || MODEL_VERSION.Q2,
      textPrompt: prompts.find((prompt) => prompt.type === 'text')?.content || '',
      imagePrompts,
      videoPrompt: {
        from: rangeFrom,
        to: rangeTo,
        duration: sourceDuration,
        content,
        sourceMeta,
      },
      settings: {
        sample_count: task.settings?.sample_count ?? 1,
        schedule_mode: task.settings?.schedule_mode ?? 'normal',
        duration: task.settings?.duration ?? 4,
        model_version: task.settings?.model_version ?? MODEL_VERSION.Q2,
        transition: task.settings?.transition ?? 'extendspeed',
        resolution: task.settings?.resolution ?? '1080p',
      },
    },
  };
};
